import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable, finalize, shareReplay } from 'rxjs';
import {
  EquipmentCategory, EquipmentRateCard, EquipmentResource, EquipmentUsage,
  FieldBulkApproval, FieldBulkApprovalResult, FieldEquipmentAllocation, FieldEquipmentRequirement,
  FieldCapability, FieldFinanceLink, FieldLaborAssignment, FieldLaborCandidate,
  FieldLaborRequirement, FieldLaborRoleOption, FieldLaborWorkEntry, FieldProficiencyLevel,
  FieldSettlementSummary, FieldWorker, FieldWorkerCapability, FieldWorkAction, FieldWorkActivityTemplate,
  FieldWorkEvidenceUpload,
  FieldWorkPackage, FieldWorkSummary, FieldWorkTargetChange, FieldWorkPage,
  QuickDeliveryHandlingPolicy, QuickDeliveryHandlingRequest, QuickDeliveryHandlingResult
} from './field-work.models';

@Injectable({ providedIn: 'root' })
export class FieldWorkService {
  private readonly http = inject(HttpClient);
  private readonly url = `${environment.restApiUrl}operations/field-work`;
  private readonly packageRequests = new Map<string, Observable<FieldWorkPackage[]>>();

  summary(owningCompanyId?: number) {
    return this.http.get<FieldWorkSummary>(`${this.url}/summary`, { params: this.params({ owningCompanyId }) });
  }

  packages(filters: { owningCompanyId?: number; targetType?: string; targetId?: number } = {}) {
    const params = this.params(filters);
    const key = params.toString();
    const inFlight = this.packageRequests.get(key);
    if (inFlight) return inFlight;
    const request = this.http.get<FieldWorkPackage[]>(`${this.url}/work-packages`, { params }).pipe(
      finalize(() => this.packageRequests.delete(key)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.packageRequests.set(key, request);
    return request;
  }

  shipmentWorkforcePreview(shipmentId: number) {
    return this.http.get<FieldWorkPackage>(`${this.url}/shipment-workforce/${shipmentId}/preview`);
  }

  packageById(id: number, owningCompanyId?: number) {
    return this.http.get<FieldWorkPackage>(`${this.url}/work-packages/${id}`, {
      params: this.params({ owningCompanyId })
    });
  }

  activityTemplates() {
    return this.http.get<FieldWorkActivityTemplate[]>(`${this.url}/activity-templates`);
  }

  quickDeliveryHandlingPolicy(owningCompanyId?: number) {
    return this.http.get<QuickDeliveryHandlingPolicy>(`${this.url}/delivery-handling/policy`, {
      params: this.params({ owningCompanyId })
    });
  }

  saveQuickDeliveryHandlingPolicy(value: QuickDeliveryHandlingPolicy) {
    return this.http.put<QuickDeliveryHandlingPolicy>(`${this.url}/delivery-handling/policy`, value);
  }

  completeQuickDeliveryHandling(deliveryId: number, value: QuickDeliveryHandlingRequest) {
    const request = { ...value, workDate: this.dateOnly(value.workDate) };
    return this.http.post<QuickDeliveryHandlingResult>(
      `${this.url}/delivery-handling/${deliveryId}/complete`, request);
  }

  laborRoles(owningCompanyId?: number) {
    return this.http.get<FieldLaborRoleOption[]>(`${this.url}/setup/roles`, {
      params: this.params({ owningCompanyId })
    });
  }

  saveFieldRole(value: FieldLaborRoleOption) {
    return value.fieldRoleId
      ? this.http.put<FieldLaborRoleOption>(`${this.url}/setup/roles/${value.fieldRoleId}`, value)
      : this.http.post<FieldLaborRoleOption>(`${this.url}/setup/roles`, value);
  }

  capabilities(owningCompanyId?: number) {
    return this.http.get<FieldCapability[]>(`${this.url}/setup/capabilities`, {
      params: this.params({ owningCompanyId })
    });
  }

  saveCapability(value: FieldCapability) {
    return value.fieldCapabilityId
      ? this.http.put<FieldCapability>(`${this.url}/setup/capabilities/${value.fieldCapabilityId}`, value)
      : this.http.post<FieldCapability>(`${this.url}/setup/capabilities`, value);
  }

  proficiencyLevels(owningCompanyId?: number) {
    return this.http.get<FieldProficiencyLevel[]>(`${this.url}/setup/proficiency-levels`, {
      params: this.params({ owningCompanyId })
    });
  }

  saveProficiencyLevel(value: FieldProficiencyLevel) {
    return value.fieldProficiencyLevelId
      ? this.http.put<FieldProficiencyLevel>(
        `${this.url}/setup/proficiency-levels/${value.fieldProficiencyLevelId}`, value)
      : this.http.post<FieldProficiencyLevel>(`${this.url}/setup/proficiency-levels`, value);
  }

  people(filters: { owningCompanyId?: number; page?: number; pageSize?: number; search?: string } | number = {}) {
    const request = typeof filters === 'number' ? { owningCompanyId: filters } : filters;
    return this.http.get<FieldWorkPage<FieldWorker>>(`${this.url}/people`, {
      params: this.params({
        owningCompanyId: request.owningCompanyId,
        page: request.page ?? 0,
        pageSize: request.pageSize ?? 20,
        search: request.search
      })
    });
  }

  savePerson(value: FieldWorker) {
    return value.fieldWorkerId
      ? this.http.put<FieldWorker>(`${this.url}/people/${value.fieldWorkerId}`, value)
      : this.http.post<FieldWorker>(`${this.url}/people`, value);
  }

  saveWorkerCapability(workerId: number, value: FieldWorkerCapability) {
    const request = { ...value };
    if (value.acquiredOn) request.acquiredOn = this.dateOnly(value.acquiredOn);
    if (value.expiresOn) request.expiresOn = this.dateOnly(value.expiresOn);
    return value.fieldWorkerCapabilityId
      ? this.http.put<FieldWorkerCapability>(
        `${this.url}/people/${workerId}/capabilities/${value.fieldWorkerCapabilityId}`, request)
      : this.http.post<FieldWorkerCapability>(`${this.url}/people/${workerId}/capabilities`, request);
  }

  savePackage(value: FieldWorkPackage) {
    return value.fieldWorkPackageId
      ? this.http.put<FieldWorkPackage>(`${this.url}/work-packages/${value.fieldWorkPackageId}`, value)
      : this.http.post<FieldWorkPackage>(`${this.url}/work-packages`, value);
  }

  transition(id: number, status: string, action: FieldWorkAction = {}) {
    return this.http.post<FieldWorkPackage>(`${this.url}/work-packages/${id}/status/${status}`, action);
  }

  transferPackage(id: number, change: FieldWorkTargetChange) {
    return this.http.post<FieldWorkPackage>(`${this.url}/work-packages/${id}/transfer`, change);
  }

  splitPackage(id: number, change: FieldWorkTargetChange) {
    return this.http.post<FieldWorkPackage>(`${this.url}/work-packages/${id}/split`, change);
  }

  saveLaborRequirement(packageId: number, value: FieldLaborRequirement) {
    return value.fieldLaborRequirementId
      ? this.http.put<FieldLaborRequirement>(
        `${this.url}/work-packages/${packageId}/labor-requirements/${value.fieldLaborRequirementId}`, value)
      : this.http.post<FieldLaborRequirement>(`${this.url}/work-packages/${packageId}/labor-requirements`, value);
  }

  removeLaborRequirement(packageId: number, requirementId: number) {
    return this.http.delete<void>(
      `${this.url}/work-packages/${packageId}/labor-requirements/${requirementId}`);
  }

  laborCandidates(packageId: number, requirementId: number) {
    return this.http.get<FieldLaborCandidate[]>(
      `${this.url}/work-packages/${packageId}/labor-requirements/${requirementId}/candidates`);
  }

  assignLabor(packageId: number, requirementId: number, value: FieldLaborAssignment) {
    return this.http.post<FieldLaborAssignment>(
      `${this.url}/work-packages/${packageId}/labor-requirements/${requirementId}/assignments`, value);
  }

  clearLaborSlot(packageId: number, requirementId: number, slotNumber: number) {
    return this.http.delete<void>(
      `${this.url}/work-packages/${packageId}/labor-requirements/${requirementId}/slots/${slotNumber}`);
  }

  laborWorkEntries(assignmentId?: number, owningCompanyId?: number) {
    return this.http.get<FieldLaborWorkEntry[]>(`${this.url}/labor/work-entries`, {
      params: this.params({ owningCompanyId, assignmentId })
    });
  }

  uploadEvidence(value: FieldWorkEvidenceUpload) {
    return this.http.post<FieldWorkEvidenceUpload>(`${this.url}/evidence`, value);
  }

  saveLaborWorkEntry(value: FieldLaborWorkEntry) {
    return value.fieldLaborWorkEntryId
      ? this.http.put<FieldLaborWorkEntry>(
        `${this.url}/labor/work-entries/${value.fieldLaborWorkEntryId}`, value)
      : this.http.post<FieldLaborWorkEntry>(`${this.url}/labor/work-entries`, value);
  }

  laborWorkEntryAction(id: number, action: string, value: FieldWorkAction = {}) {
    return this.http.post<FieldLaborWorkEntry>(`${this.url}/labor/work-entries/${id}/${action}`, value);
  }

  categories(owningCompanyId?: number) {
    return this.http.get<EquipmentCategory[]>(`${this.url}/equipment/categories`, {
      params: this.params({ owningCompanyId })
    });
  }

  saveCategory(value: EquipmentCategory) {
    return value.equipmentCategoryId
      ? this.http.put<EquipmentCategory>(`${this.url}/equipment/categories/${value.equipmentCategoryId}`, value)
      : this.http.post<EquipmentCategory>(`${this.url}/equipment/categories`, value);
  }

  resources(owningCompanyId?: number) {
    return this.http.get<EquipmentResource[]>(`${this.url}/equipment/resources`, {
      params: this.params({ owningCompanyId })
    });
  }

  saveResource(value: EquipmentResource) {
    return value.equipmentResourceId
      ? this.http.put<EquipmentResource>(`${this.url}/equipment/resources/${value.equipmentResourceId}`, value)
      : this.http.post<EquipmentResource>(`${this.url}/equipment/resources`, value);
  }

  rates(owningCompanyId?: number) {
    return this.http.get<EquipmentRateCard[]>(`${this.url}/equipment/rate-cards`, {
      params: this.params({ owningCompanyId })
    });
  }

  saveRate(value: EquipmentRateCard) {
    return value.equipmentRateCardId
      ? this.http.put<EquipmentRateCard>(`${this.url}/equipment/rate-cards/${value.equipmentRateCardId}`, value)
      : this.http.post<EquipmentRateCard>(`${this.url}/equipment/rate-cards`, value);
  }

  saveEquipmentRequirement(packageId: number, value: FieldEquipmentRequirement) {
    return value.fieldEquipmentRequirementId
      ? this.http.put<FieldEquipmentRequirement>(
        `${this.url}/work-packages/${packageId}/equipment-requirements/${value.fieldEquipmentRequirementId}`, value)
      : this.http.post<FieldEquipmentRequirement>(
        `${this.url}/work-packages/${packageId}/equipment-requirements`, value);
  }

  equipmentCandidates(packageId: number, requirementId: number) {
    return this.http.get<EquipmentResource[]>(
      `${this.url}/work-packages/${packageId}/equipment-requirements/${requirementId}/candidates`);
  }

  allocateEquipment(packageId: number, requirementId: number, value: FieldEquipmentAllocation) {
    return this.http.post<FieldEquipmentAllocation>(
      `${this.url}/work-packages/${packageId}/equipment-requirements/${requirementId}/allocations`, value);
  }

  equipmentAction(id: number, action: string, value: FieldWorkAction = {}) {
    return this.http.post<FieldEquipmentAllocation>(`${this.url}/equipment/allocations/${id}/${action}`, value);
  }

  usages(allocationId: number, owningCompanyId?: number) {
    return this.http.get<EquipmentUsage[]>(`${this.url}/equipment/allocations/${allocationId}/usages`, {
      params: this.params({ owningCompanyId })
    });
  }

  equipmentUsages(status?: string, owningCompanyId?: number) {
    return this.http.get<EquipmentUsage[]>(`${this.url}/equipment/usages`, {
      params: this.params({ owningCompanyId, status })
    });
  }

  saveUsage(value: EquipmentUsage) {
    return value.fieldEquipmentUsageId
      ? this.http.put<EquipmentUsage>(`${this.url}/equipment/usages/${value.fieldEquipmentUsageId}`, value)
      : this.http.post<EquipmentUsage>(`${this.url}/equipment/usages`, value);
  }

  usageAction(id: number, action: string, value: FieldWorkAction = {}) {
    return this.http.post<EquipmentUsage>(`${this.url}/equipment/usages/${id}/${action}`, value);
  }

  importLegacyPosm(scheduleId: number) {
    return this.http.post<FieldWorkPackage>(
      `${this.url}/posm/delivery-schedules/${scheduleId}/import-legacy-resources`, {});
  }

  financeExceptions(owningCompanyId?: number) {
    return this.http.get<FieldFinanceLink[]>(`${this.url}/finance/exceptions`, {
      params: this.params({ owningCompanyId })
    });
  }

  retryFinanceException(id: number, sourceKind = 'FIELD_EQUIPMENT', owningCompanyId?: number) {
    return this.http.post<FieldFinanceLink>(`${this.url}/finance/exceptions/${id}/retry`, {}, {
      params: this.params({ sourceKind, owningCompanyId })
    });
  }

  settlementSummaries(owningCompanyId?: number) {
    return this.http.get<FieldSettlementSummary[]>(`${this.url}/settlements`, {
      params: this.params({ owningCompanyId })
    });
  }

  bulkApproval(value: FieldBulkApproval) {
    return this.http.post<FieldBulkApprovalResult>(`${this.url}/approvals/bulk`, value);
  }

  private params(values: Record<string, string | number | boolean | null | undefined>): HttpParams {
    let params = new HttpParams();
    Object.entries(values).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') params = params.set(key, String(value));
    });
    return params;
  }

  private dateOnly(value?: string | Date): string | undefined {
    if (!value) return undefined;
    if (typeof value === 'string') return value.includes('T') ? value.slice(0, 10) : value;
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
